// 1. React imports
import React, { useState, useMemo, useEffect, useRef } from 'react'

// 2. External library imports
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  Button,
  Menu,
  Tooltip,
} from '@mui/material'
import { Search, ErrorOutline, ArrowDropDown, WarningAmber, Toc } from '@mui/icons-material'
import dayjs, { Dayjs } from 'dayjs'

// 3. Internal imports
import { useCloudWatchLogs } from '@/hooks/react-query/useCloudWatchLogs'
import { useCloudWatchCounts } from '@/hooks/react-query/useCloudWatchCounts'
import { CloudWatchTableRow } from '@/types/cloudWatch.type'
import Loading from '@/components/shared/layout/Loading'
import { ErrorDisplay } from '@/components/shared/ErrorBoundary'
import { DateRangePicker } from '@/components/shared/DateRangePicker'
import { Workstream } from '@/utils/workstreamHelper'
import { DaylightDateFormat } from '@/constants/DaylightDateFormat'
import { zIndexes } from '@/utils/zIndexes'
import { displayMessage } from '@/constants/strings'
import { toast } from '@/utils/snackbarHelper'
import { handleExportError } from '@/utils/exportErrorHandler'
import { exportCloudWatchToCSV, exportCloudWatchToXLSX, generateCloudWatchExportFilename } from '@/utils/exportUtils'
import csvIcon from '@/assets/csv-icon.svg'
import xlsxIcon from '@/assets/xlsx-icon.svg'

export const Component = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const workstreamFromUrl = (searchParams.get('workstream') as Workstream) || 'backoffice'

  const [searchQuery, setSearchQuery] = useState('')
  const [envFilter, setEnvFilter] = useState('dev')
  const [errorTypeFilter, setErrorTypeFilter] = useState<'4xx' | '5xx' | 'all'>('all')
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([dayjs().subtract(1, 'day'), dayjs()])
  const [workstreamFilter, setWorkstreamFilter] = useState<Workstream>(workstreamFromUrl)

  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [currentPage, setCurrentPage] = useState(0)
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null)
  const [orderBy, setOrderBy] = useState<'error_code' | 'lambda_function' | 'timestamp' | null>(null)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [nextCursor, setNextCursor] = useState<string | null>(null) // Cursor for next batch of 2000
  const [allLoadedRows, setAllLoadedRows] = useState<CloudWatchTableRow[]>([]) // Accumulated rows from all batches
  const loadedBatchesRef = useRef<Map<string, CloudWatchTableRow[]>>(new Map()) // Track loaded batches by cursor to allow updates

  // Redirect to add workstream parameter if missing
  useEffect(() => {
    if (!searchParams.get('workstream')) {
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.set('workstream', 'backoffice')
      navigate(`/cloud-watch?${newSearchParams.toString()}`, { replace: true })
    }
  }, [searchParams, navigate])

  // Sync workstreamFilter with URL changes
  useEffect(() => {
    setWorkstreamFilter(workstreamFromUrl)
  }, [workstreamFromUrl])

  // Format dates for API
  const startDate = useMemo(() => {
    return dateRange[0]
      ? dateRange[0].format(DaylightDateFormat.ISO_DATE)
      : dayjs().subtract(1, 'day').format(DaylightDateFormat.ISO_DATE)
  }, [dateRange])

  const endDate = useMemo(() => {
    return dateRange[1] ? dateRange[1].format(DaylightDateFormat.ISO_DATE) : dayjs().format(DaylightDateFormat.ISO_DATE)
  }, [dateRange])

  // Convert workstream format (linehaul -> linehaul, backoffice -> back-office, dock-automation -> dockauto)
  const apiWorkstream = useMemo(() => {
    if (workstreamFilter === 'backoffice') return 'back-office'
    if (workstreamFilter === 'linehaul') return 'linehaul'
    if (workstreamFilter === 'dispatch') return 'dispatch-web'
    if (workstreamFilter === 'dispatch-mobile') return 'dispatch-mobile'
    if (workstreamFilter === 'dock-automation') return 'dockauto'
    return workstreamFilter || 'linehaul'
  }, [workstreamFilter])

  // Fetch CloudWatch logs
  const {
    data: cloudWatchData,
    isLoading,
    isError,
  } = useCloudWatchLogs({
    workstream: apiWorkstream,
    statusCodes: errorTypeFilter,
    startDate,
    endDate,
    environment: envFilter !== 'all' ? envFilter : undefined,
    cursor: nextCursor,
  })

  // Fetch CloudWatch error counts (always fetch all error types for the cards)
  const {
    data: cloudWatchCounts,
    isLoading: isCountsLoading,
    isError: isCountsError,
  } = useCloudWatchCounts({
    workstream: apiWorkstream,
    statusCodes: 'all', // Always fetch all errors for count cards
    startDate,
    endDate,
    environment: envFilter !== 'all' ? envFilter : undefined,
  })

  // Reset page, selections and cursor when filters change
  useEffect(() => {
    setCurrentPage(0)
    setNextCursor(null)
    setAllLoadedRows([]) // Reset accumulated rows
    loadedBatchesRef.current = new Map() // Reset loaded batches tracker
  }, [envFilter, errorTypeFilter, startDate, endDate, workstreamFilter])

  // Accumulate rows from all loaded batches
  // Store each batch by cursor key to allow refetch updates without duplicates
  useEffect(() => {
    // Only process data when not loading and data exists
    if (!isLoading && cloudWatchData?.tableRows !== undefined) {
      const cursorKey = nextCursor || 'initial'

      // Store/update this batch in the map
      loadedBatchesRef.current.set(cursorKey, cloudWatchData.tableRows)

      // Rebuild allLoadedRows from all batches in order
      const orderedBatches: CloudWatchTableRow[] = []

      // First, add the initial batch if it exists
      if (loadedBatchesRef.current.has('initial')) {
        orderedBatches.push(...(loadedBatchesRef.current.get('initial') || []))
      }

      // Then add all other batches (excluding 'initial')
      for (const [key, batch] of loadedBatchesRef.current.entries()) {
        if (key !== 'initial') {
          orderedBatches.push(...batch)
        }
      }

      setAllLoadedRows(orderedBatches)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudWatchData, isLoading])

  // Filter and sort table rows (use accumulated rows from all batches)
  const filteredRows = useMemo(() => {
    if (!allLoadedRows.length) return []

    let filtered = allLoadedRows

    // Apply search filter
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (row) =>
          row.error_message.toLowerCase().includes(lowerQuery) ||
          row.lambda_function.toLowerCase().includes(lowerQuery) ||
          row.stack_name.toLowerCase().includes(lowerQuery)
      )
    }

    // Apply sorting
    if (orderBy) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: string | number
        let bValue: string | number

        switch (orderBy) {
          case 'error_code':
            aValue = a.error_code
            bValue = b.error_code
            break
          case 'lambda_function':
            aValue = a.lambda_function.toLowerCase()
            bValue = b.lambda_function.toLowerCase()
            break
          case 'timestamp':
            aValue = new Date(a.timestamp).getTime()
            bValue = new Date(b.timestamp).getTime()
            break
          default:
            return 0
        }

        if (aValue < bValue) return order === 'asc' ? -1 : 1
        if (aValue > bValue) return order === 'asc' ? 1 : -1
        return 0
      })
    }

    return filtered
  }, [allLoadedRows, searchQuery, orderBy, order])

  // Paginate filtered rows
  const paginatedRows = useMemo(() => {
    const startIndex = currentPage * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return filteredRows.slice(startIndex, endIndex)
  }, [filteredRows, currentPage, rowsPerPage])

  // Derived server-side pagination state
  const serverPagination = cloudWatchData?.pagination ?? null
  const hasMoreFromServer = serverPagination?.has_more === true
  const totalCount = cloudWatchCounts?.total_errors ?? 0

  const handleLoadNextBatch = () => {
    if (serverPagination?.next_cursor) {
      setNextCursor(serverPagination.next_cursor)
      // Don't reset currentPage - user stays on current page position
    }
  }

  // Handle sort
  const handleRequestSort = (property: 'error_code' | 'lambda_function' | 'timestamp') => {
    if (orderBy === property) {
      if (order === 'asc') {
        setOrder('desc')
      } else {
        setOrderBy(null)
        setOrder('asc')
      }
    } else {
      setOrderBy(property)
      setOrder('asc')
    }
  }

  // Handle pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    const totalLocalPages = Math.ceil(filteredRows.length / rowsPerPage)
    if (hasMoreFromServer && newPage >= totalLocalPages) {
      // User clicked ">" on the last page of the current batch — fetch next server batch
      handleLoadNextBatch()
    } else {
      setCurrentPage(newPage)
    }
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setCurrentPage(0)
  }

  // Export handlers
  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportMenuAnchor(event.currentTarget)
  }

  const handleExportClose = () => {
    setExportMenuAnchor(null)
  }

  const handleExportCSV = () => {
    try {
      if (filteredRows.length === 0) {
        toast('No logs to export', { variant: 'info' })
        handleExportClose()
        return
      }

      const filename = generateCloudWatchExportFilename('csv', apiWorkstream, startDate, endDate)
      exportCloudWatchToCSV(filteredRows, filename)
      toast('CloudWatch logs exported successfully', { variant: 'success' })
      handleExportClose()
    } catch (error) {
      handleExportError(error, 'CloudWatch CSV export')
    }
  }

  const handleExportXLSX = () => {
    try {
      if (filteredRows.length === 0) {
        toast('No logs to export', { variant: 'info' })
        handleExportClose()
        return
      }

      const filename = generateCloudWatchExportFilename('xlsx', apiWorkstream, startDate, endDate)
      exportCloudWatchToXLSX(filteredRows, filename)
      toast('CloudWatch logs exported successfully', { variant: 'success' })
      handleExportClose()
    } catch (error) {
      handleExportError(error, 'CloudWatch XLSX export')
    }
  }

  // Error state
  if (isError && !isLoading) {
    return (
      <ErrorDisplay
        primaryMessage={displayMessage.cloudWatch.error}
        secondaryMessage={displayMessage.cloudWatch.errorSubtext}
        showReloadOption
      />
    )
  }

  return (
    <Box
      sx={{
        width: '100%',
        px: { xs: 2, sm: 3 },
        paddingBottom: { xs: 2, sm: 3 },
        paddingTop: { xs: 2, sm: 3 },
        backgroundColor: '#fafafa',
      }}
    >
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        {/* Priority Cards */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* 400 Errors Card */}
          <Paper
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200, md: 200 },
              borderRadius: 2,
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#FFFFFF',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: '#D32F2F',
              }}
            />
            <Box sx={{ p: 2, pl: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#666',
                  }}
                >
                  400 Errors
                </Typography>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: '#FEF3F2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #FECECE',
                  }}
                >
                  <ErrorOutline sx={{ fontSize: 24, color: '#B81C1C' }} />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 2,
                  mb: 1.5,
                }}
              >
                {isCountsLoading ? (
                  <Loading size="2rem" />
                ) : isCountsError ? (
                  <Tooltip title="Failed to load count data">
                    <ErrorOutline sx={{ fontSize: 32, color: '#d32f2f' }} />
                  </Tooltip>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#333',
                        lineHeight: 1.2,
                      }}
                    >
                      {cloudWatchCounts?.count_4xx || 0}
                    </Typography>
                  </>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1,
                  width: '65%',
                }}
              ></Box>
            </Box>
          </Paper>

          {/* 500 Errors Card */}
          <Paper
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200, md: 200 },
              borderRadius: 2,
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#FFFFFF',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: '#A16107',
              }}
            />
            <Box sx={{ p: 2, pl: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#666',
                  }}
                >
                  500 Errors
                </Typography>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: '#FEFCE7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #EAE1A0',
                  }}
                >
                  <WarningAmber sx={{ fontSize: 24, color: '#A16107' }} />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 2,
                  mb: 1.5,
                }}
              >
                {isCountsLoading ? (
                  <Loading size="2rem" />
                ) : isCountsError ? (
                  <Tooltip title="Failed to load count data">
                    <ErrorOutline sx={{ fontSize: 32, color: '#d32f2f' }} />
                  </Tooltip>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#333',
                        lineHeight: 1.2,
                      }}
                    >
                      {cloudWatchCounts?.count_5xx || 0}
                    </Typography>
                  </>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1,
                  width: '65%',
                }}
              ></Box>
            </Box>
          </Paper>

          {/* Total Errors Card */}
          <Paper
            sx={{
              flex: 1,
              minWidth: { xs: '100%', sm: 200, md: 200 },
              borderRadius: 2,
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#FFFFFF',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '6px',
                backgroundColor: '#2E7D32',
              }}
            />
            <Box sx={{ p: 2, pl: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#666',
                  }}
                >
                  Total Errors
                </Typography>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    backgroundColor: '#2E7D321F',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #2E7D324D',
                  }}
                >
                  <Toc sx={{ fontSize: 30, color: '#2E7D32' }} />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 2,
                  mb: 1.5,
                }}
              >
                {isCountsLoading ? (
                  <Loading size="2rem" />
                ) : isCountsError ? (
                  <Tooltip title="Failed to load count data">
                    <ErrorOutline sx={{ fontSize: 32, color: '#d32f2f' }} />
                  </Tooltip>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#333',
                        lineHeight: 1.2,
                      }}
                    >
                      {cloudWatchCounts?.total_errors || 0}
                    </Typography>
                  </>
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1,
                  width: '65%',
                }}
              ></Box>
            </Box>
          </Paper>
        </Box>

        {/* Search, Filters Row */}
        <Box
          sx={{
            width: '100%',
            border: '1px solid',
            borderRadius: '12px',
            color: '#FFFFFF',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 0px 4px 0px #00000040',
            position: 'sticky',
            top: 0,
            zIndex: zIndexes.tableHeader,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2 },
              mb: { xs: 2, sm: 3 },
              flexWrap: 'wrap',
              flexDirection: { xs: 'column', sm: 'row' },
              paddingTop: { xs: '16px', sm: '24px' },
              paddingLeft: { xs: '16px', sm: '24px' },
              paddingRight: { xs: '16px', sm: '24px' },
              paddingBottom: { xs: '8px', sm: '4px' },
            }}
          >
            <TextField
              size="small"
              placeholder="Search Issues"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search sx={{ color: '#9E9E9E' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { xs: '100%', sm: 300 },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  borderRadius: '4px',
                },
              }}
            />

            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 180 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <InputLabel>Environment</InputLabel>
              <Select value={envFilter} label="Environment" onChange={(e) => setEnvFilter(e.target.value)}>
                <MenuItem value="dev">Dev</MenuItem>
                <MenuItem value="prod">Prod</MenuItem>
                <MenuItem value="qa">QA</MenuItem>
                <MenuItem value="uat">UAT</MenuItem>
                <MenuItem value="tst">TST</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 180 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <InputLabel>Error Type</InputLabel>
              <Select
                value={errorTypeFilter}
                label="Error Type"
                onChange={(e) => setErrorTypeFilter(e.target.value as '4xx' | '5xx' | 'all')}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="4xx">4xx Errors</MenuItem>
                <MenuItem value="5xx">5xx Errors</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <DateRangePicker
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onDateChange={(start, end) => {
                  setDateRange([start, end])
                }}
                maxDaysRange={30}
                showLastYearOption={false}
              />
            </Box>

            <Box
              sx={{
                flexGrow: { xs: 0, sm: 1 },
                width: { xs: '100%', sm: 'auto' },
              }}
            />

            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowDropDown />}
              onClick={handleExportClick}
              sx={{
                textTransform: 'none',
                px: 2,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={handleExportClose}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: 1,
                    mt: 0.5,
                    minWidth: 150,
                  },
                },
              }}
            >
              <MenuItem
                onClick={handleExportXLSX}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <Box
                  component="img"
                  src={xlsxIcon}
                  alt="XLSX"
                  sx={{
                    width: 20,
                    height: 20,
                    mr: 1.5,
                  }}
                />
                <Typography sx={{ fontSize: '14px', color: '#333' }}>XLSX</Typography>
              </MenuItem>
              <MenuItem
                onClick={handleExportCSV}
                sx={{
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <Box
                  component="img"
                  src={csvIcon}
                  alt="CSV"
                  sx={{
                    width: 20,
                    height: 20,
                    mr: 1.5,
                  }}
                />
                <Typography sx={{ fontSize: '14px', color: '#333' }}>CSV</Typography>
              </MenuItem>
            </Menu>
          </Box>

          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 1,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: {
                xs: 'calc(100vh - 400px)',
                md: 'calc(100vh - 300px)',
              },
              overflowX: 'auto',
            }}
          >
            <Table sx={{ tableLayout: 'fixed', minWidth: 1400 }}>
              <TableHead
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: zIndexes.tableSubHeader,
                  backgroundColor: '#FFFFFF',
                }}
              >
                <TableRow sx={{ letterSpacing: '0.4px' }}>
                  <TableCell sx={{ fontSize: '14px', width: '30%', paddingLeft: '30px' }}>Error Message</TableCell>
                  <TableCell
                    sx={{
                      fontSize: '14px',
                      width: '10%',
                      fontWeight: 700,
                      color: '#333',
                      letterSpacing: '0.4px',
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'error_code'}
                      direction={orderBy === 'error_code' ? order : 'asc'}
                      onClick={() => handleRequestSort('error_code')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: orderBy === 'error_code' ? 1 : 0.3,
                          fontSize: '20px',
                          color: '#000000',
                        },
                        fontWeight: 700,
                        fontSize: '14px',
                        color: '#333',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Error Code
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontSize: '14px', width: '15%' }}>
                    <TableSortLabel
                      active={orderBy === 'lambda_function'}
                      direction={orderBy === 'lambda_function' ? order : 'asc'}
                      onClick={() => handleRequestSort('lambda_function')}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          opacity: orderBy === 'lambda_function' ? 1 : 0.3,
                          fontSize: '20px',
                          color: '#000000',
                        },
                        fontWeight: 700,
                        fontSize: '14px',
                        color: '#333',
                        letterSpacing: '0.4px',
                      }}
                    >
                      Lambda Function
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '14px',
                      width: '15%',
                      fontWeight: 700,
                      color: '#333',
                      letterSpacing: '0.4px',
                    }}
                  >
                    Log Group
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '14px',
                      width: '15%',
                      fontWeight: 700,
                      color: '#333',
                      letterSpacing: '0.4px',
                    }}
                  >
                    Stack Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '14px',
                      width: '10%',
                      fontWeight: 700,
                      color: '#333',
                      letterSpacing: '0.4px',
                    }}
                  >
                    Region
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 8 }}>
                      <Loading label="CloudWatch logs" />
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 8 }}>
                      <Typography variant="body1" sx={{ color: '#666', fontSize: '16px' }}>
                        {displayMessage.cloudWatch.empty}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999', fontSize: '14px', mt: 1 }}>
                        {displayMessage.cloudWatch.emptySubtext}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f9f9f9',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          width: '35%',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          paddingLeft: '30px',
                        }}
                      >
                        <Tooltip
                          title={
                            <Box
                              sx={{
                                maxWidth: '600px',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {row.error_message}
                            </Box>
                          }
                          placement="top-start"
                          arrow
                          PopperProps={{
                            sx: {
                              zIndex: 9999,
                            },
                            modifiers: [
                              {
                                name: 'flip',
                                enabled: true,
                                options: {
                                  fallbackPlacements: ['top', 'bottom', 'right'],
                                },
                              },
                              {
                                name: 'preventOverflow',
                                enabled: true,
                                options: {
                                  rootBoundary: 'document',
                                  padding: 8,
                                },
                              },
                            ],
                            container: document.body,
                          }}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                maxWidth: 'none',
                                fontSize: '13px',
                                padding: '12px 16px',
                                backgroundColor: 'rgba(0, 0, 0, 0.92)',
                                lineHeight: 1.6,
                              },
                            },
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              cursor: 'pointer',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                color: '#333',
                                letterSpacing: '0.4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.error_message}
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '12px',
                            color: '#999',
                            letterSpacing: '0.4px',
                            display: 'block',
                            mt: 0.5,
                          }}
                        >
                          {dayjs(row.timestamp).format(DaylightDateFormat.DATE_TIME_12)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '12px',
                            backgroundColor: row.error_code.startsWith('4') ? '#FFEBEE' : '#FFF9C4',
                            color: row.error_code.startsWith('4') ? '#B81C1C' : '#A16107',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 600,
                            }}
                          >
                            {row.error_code}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: '15%' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#333',
                            fontWeight: 600,
                            wordBreak: 'break-word',
                          }}
                        >
                          {row.lambda_function}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '15%' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#666',
                            fontWeight: 500,
                            wordBreak: 'break-word',
                          }}
                        >
                          {row.log_group}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '15%' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#666',
                            fontWeight: 500,
                            wordBreak: 'break-word',
                          }}
                        >
                          {row.stack_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '12px',
                            color: '#333',
                            fontWeight: 600,
                          }}
                        >
                          {row.region}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={hasMoreFromServer ? filteredRows.length + rowsPerPage : filteredRows.length}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 75, 100]}
            labelDisplayedRows={({ from, to }) => {
              // When error type filter is active, show filtered count; otherwise show total from API
              const total =
                errorTypeFilter !== 'all' ? filteredRows.length : totalCount > 0 ? totalCount : filteredRows.length
              return `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`
            }}
            sx={{
              borderTop: '1px solid #e0e0e0',
              '& .MuiTablePagination-toolbar': {
                px: 2,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
